import { z } from 'zod';
import { $ZodType } from 'zod/v4/core';
import { common } from '@gosupersimple/types';

import { QuerySortDirection } from '../generated/graphql';

function isNullable(schema: $ZodType): boolean {
  return (
    schema instanceof z.ZodNullable ||
    (schema instanceof z.ZodOptional && isNullable(schema.def.innerType))
  );
}

/**
 * Strip each null from graphql response unless the given zod allows it.
 */
export function cleanGraphQLResponse<T extends z.ZodTypeAny>(input: unknown, schema: T): unknown {
  if (input === null) {
    if (isNullable(schema)) {
      return null;
    }
    return undefined;
  }

  if (schema instanceof z.ZodObject && typeof input === 'object' && input !== null) {
    const shape = schema.shape;
    const result: Record<string, any> = {};

    for (const key in shape) {
      const fieldSchema = shape[key];
      const rawValue = (input as any)[key];
      const cleaned = cleanGraphQLResponse(rawValue, fieldSchema);

      if (cleaned !== undefined) {
        result[key] = cleaned;
      }
    }

    return result;
  }

  if (schema instanceof z.ZodArray && Array.isArray(input)) {
    return input
      .map((item) => cleanGraphQLResponse(item, schema))
      .filter((item) => item !== undefined);
  }

  return input;
}

export function parseGraphQLResponse<T extends z.ZodTypeAny>(
  input: unknown,
  schema: T,
): z.output<T> {
  const cleanedData = cleanGraphQLResponse(input, schema);
  return schema.parse(cleanedData);
}

export function sortingToGql(sorting: common.Sorting[]) {
  return sorting.map(({ key, direction }) => {
    switch (direction) {
      case 'ASC':
        return { key, direction: QuerySortDirection.Asc };
      case 'DESC':
        return { key, direction: QuerySortDirection.Desc };
      default:
        throw new Error(`Unknown direction: ${direction}`);
    }
  });
}
